
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./Signal.css";

const BEST_COINS = [
  "BTC-USDT",
  "ETH-USDT",
  "SOL-USDT",
  "XRP-USDT",
  "DOGE-USDT",
  "TRX-USDT",
  "OKB-USDT",
  "ADA-USDT",
  "BNB-USDT",
  "AVAX-USDT",
];

const COIN_ICONS = {
  BTC: "https://assets.coingecko.com/coins/images/1/large/bitcoin.png",
  ETH: "https://assets.coingecko.com/coins/images/279/large/ethereum.png",
  SOL: "https://assets.coingecko.com/coins/images/4128/large/solana.png",
  XRP: "https://assets.coingecko.com/coins/images/44/large/xrp-symbol-white-128.png",
  DOGE: "https://assets.coingecko.com/coins/images/5/large/dogecoin.png",
  TRX: "https://assets.coingecko.com/coins/images/1094/large/tron-logo.png",
  OKB: "https://assets.coingecko.com/coins/images/4463/large/OKB.png",
  ADA: "https://assets.coingecko.com/coins/images/975/large/cardano.png",
  BNB: "https://assets.coingecko.com/coins/images/825/large/binance-coin-logo.png",
  AVAX: "https://assets.coingecko.com/coins/images/12559/large/Avalanche_Circle_RedWhite_Trans.png",
};

const getCoinIcon = (symbol) =>
  COIN_ICONS[symbol] ||
  `https://assets.coincap.io/assets/icons/${symbol.toLowerCase()}@2x.png`;

const formatNumber = (value, maximumFractionDigits = 2) =>
  Number(value).toLocaleString("en-US", {
    maximumFractionDigits,
  });

const createSignal = (coin) => {
  const last = Number(coin.last || 0);
  const open = Number(coin.sodUtc8 || 0);
  const volume = Number(coin.volCcy24h || 0);

  const change = open > 0 ? ((last - open) / open) * 100 : 0;

  let signal = "NEUTRAL";
  let strength = "Medium";
  let color = "#94a3b8";
  let reason = ["Mabadiliko ya bei ni ya kawaida"];

  if (change > 3.5) {
    signal = "STRONG BUY";
    strength = "High";
    color = "#16c784";
    reason = ["Bei imepanda kwa kasi", "Momentum inaelekea juu"];
  } else if (change > 1.2) {
    signal = "BUY";
    strength = "Medium";
    color = "#22c55e";
    reason = ["Bei inaongezeka", "Momentum inaelekea juu"];
  } else if (change < -3.5) {
    signal = "STRONG SELL";
    strength = "High";
    color = "#ef4444";
    reason = ["Bei imeshuka kwa kasi", "Momentum inaelekea chini"];
  } else if (change < -1.2) {
    signal = "SELL";
    strength = "Medium";
    color = "#f87171";
    reason = ["Bei inashuka", "Shinikizo la kuuza"];
  }

  return {
    instId: coin.instId,
    symbol: coin.instId.split("-")[0],
    price: last,
    change: change.toFixed(2),
    volume,
    signal,
    strength,
    reason,
    color,
    isBest: BEST_COINS.includes(coin.instId),
  };
};

export default function Signal() {
  const [signals, setSignals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");

  const navigate = useNavigate();

  useEffect(() => {
    let active = true;

    const fetchSignals = async () => {
      try {
        const response = await fetch(
          "https://www.okx.com/api/v5/market/tickers?instType=SPOT"
        );

        if (!response.ok) {
          throw new Error(`OKX API error: ${response.status}`);
        }

        const data = await response.json();

        if (data.code !== "0") {
          throw new Error(data.msg || "OKX API imeshindwa.");
        }

        const allCoins = data.data || [];

        const best = allCoins.filter((coin) =>
          BEST_COINS.includes(coin.instId)
        );

        const others = allCoins
          .filter((coin) => !BEST_COINS.includes(coin.instId))
          .sort(
            (a, b) =>
              Number(b.volCcy24h || 0) -
              Number(a.volCcy24h || 0)
          )
          .slice(0, 80);

        const processed = [...best, ...others].map(createSignal);

        if (active) {
          setSignals(processed);
          setError("");
        }
      } catch (err) {
        console.error("Imeshindwa kupata signals:", err);

        if (active) {
          setError("Imeshindwa kupakia data. Jaribu tena.");
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    fetchSignals();

    const timer = setInterval(fetchSignals, 7000);

    return () => {
      active = false;
      clearInterval(timer);
    };
  }, []);

  const filteredSignals = signals
    .filter((item) =>
      item.instId.toLowerCase().includes(search.toLowerCase().trim())
    )
    .filter((item) => {
      if (filter === "buy") return item.signal.includes("BUY");
      if (filter === "sell") return item.signal.includes("SELL");
      return true;
    });

  const openCoinChart = (instId) => {
    navigate(`/chart/${encodeURIComponent(instId)}`);
  };

  return (
    <main className="signal-container">
      <header className="signal-page-header">
        <div>
          <h2>Trading Signals</h2>
          <p className="subtitle">
            Best Coins + Top Volume · 24H Market
          </p>
        </div>

        <div className="signal-live">
          <span className="live-dot" />
          Live
        </div>
      </header>

      <div className="signal-tools">
        <input
          type="search"
          className="signal-search"
          placeholder="⌕ Search BTC, ETH, SOL..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search coins"
        />

        <div className="signal-filters">
          {[
            ["all", "All"],
            ["buy", "BuySignal"],
            ["sell", "SellSignal"],
          ].map(([value, label]) => (
            <button
              key={value}
              type="button"
              className={filter === value ? "active" : ""}
              onClick={() => setFilter(value)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {loading && (
        <p className="signal-message">
          Inapakua signals...
        </p>
      )}

      {!loading && error && signals.length === 0 && (
        <div className="signal-message">
          <p>{error}</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
          >
            Jaribu tena
          </button>
        </div>
      )}

      <div className="signals-summary">
        <span>
          Coins: <strong>{filteredSignals.length}</strong>
        </span>
        <span>
          Buy:{" "}
          <strong className="summary-buy">
            {filteredSignals.filter((item) =>
              item.signal.includes("BUY")
            ).length}
          </strong>
        </span>
        <span>
          Sell:{" "}
          <strong className="summary-sell">
            {filteredSignals.filter((item) =>
              item.signal.includes("SELL")
            ).length}
          </strong>
        </span>
      </div>

      <section className="signals-grid">
        {filteredSignals.map((item) => {
          const isBuy = item.signal.includes("BUY");
          const isSell = item.signal.includes("SELL");

          return (
            <article
              className={`signal-card ${
                isBuy ? "buy-card" : isSell ? "sell-card" : "neutral-card"
              }`}
              key={item.instId}
              onClick={() => openCoinChart(item.instId)}
              role="button"
              tabIndex={0}
              aria-label={`Fungua chart ya ${item.instId}`}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  openCoinChart(item.instId);
                }
              }}
            >
              <div className="signal-card-top">
                <div className="coin-info">
                  <img
                    src={getCoinIcon(item.symbol)}
                    alt=""
                    className="coin-icon"
                    loading="lazy"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src =
                        "https://cryptologos.cc/logos/bitcoin-btc-logo.png";
                    }}
                  />

                  <div className="coin-name-wrap">
                    <h3>{item.symbol}</h3>
                    <span className="coin-pair">/USDT</span>
                  </div>
                </div>

                {item.isBest && (
                  <span className="best-badge">★ Best</span>
                )}
              </div>

              <div className="signal-price-row">
                <strong className="coin-price">
                  ${formatNumber(item.price, 8)}
                </strong>

                <span
                  className="signal-change"
                  style={{ color: item.color }}
                >
                  {Number(item.change) >= 0 ? "▲" : "▼"}
                  {" "}
                  {Math.abs(Number(item.change)).toFixed(2)}%
                </span>
              </div>

              <div className="signal-card-bottom">
                <span
                  className={`signal-pill ${
                    isBuy ? "pill-buy" : isSell ? "pill-sell" : "pill-neutral"
                  }`}
                >
                  {item.signal}
                </span>

                <span className="signal-strength">
                  {item.strength}
                </span>
              </div>

              <div className="signal-reason">
                {item.reason.slice(0, 2).map((reason, index) => (
                  <span key={index} className="reason-item">
                    {reason}
                  </span>
                ))}
              </div>

              <div className="signal-card-footer">
                <span className="volume-note">
                  Vol: ${formatNumber(item.volume)}
                </span>
                <span className="open-chart-hint">
                  Chart ↗
                </span>
              </div>
            </article>
          );
        })}
      </section>

      {!loading && filteredSignals.length === 0 && !error && (
        <p className="signal-message">
          Hakuna coin iliyopatikana kwa utafutaji huu.
        </p>
      )}

      <p className="signal-disclaimer">
        Signals zinatokana na mabadiliko ya bei ya 24H pekee.
        Si ushauri wa kifedha wala uhakika wa faida.
      </p>
    </main>
  );
}
